import React, { useState, useEffect, useRef, createContext, useContext, lazy, Suspense } from "react";
import "./App.css";
import "./premium-design-system.css";
import "./styles/polish-global.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, Navigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { Badge } from "./components/ui/badge";
import { Separator } from "./components/ui/separator";
import { Alert, AlertDescription } from "./components/ui/alert";
import { 
  FileText, Users, Zap, Plus, Eye, DollarSign, Calendar, Mail, 
  Crown, Settings, LogOut, Download, CreditCard, CheckCircle, AlertCircle,
  User, Building, Globe, Lock
} from "lucide-react";
import { toast, Toaster } from "sonner";
import { loadStripe } from '@stripe/stripe-js';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { RegionProvider } from './contexts/RegionContext';
import { DisplayCurrencyProvider } from './contexts/DisplayCurrencyContext';
import { MARKETS as GEO_MARKETS } from './geo/markets';
import { ABTestingProvider } from './contexts/useABTesting';

// Core components (always loaded)
import LanguageHead from './components/LanguageHead';
import PremiumLogin from './components/PremiumLogin';
import PremiumRegister from './components/PremiumRegister';
import { authErrorMessage } from './utils/authErrors';
const ForgotPassword = lazy(() => import('./components/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));

// Iter 123 — alias /signup → /register en conservant ?lang=, ?plan=, ?next=… et le hash
const SignupRedirect = () => {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: '/register', search, hash }} replace />;
};

// Deferred banner — loaded lazily so it never touches critical-path bundle / LCP
// Bandeau de suggestion de langue : rendu dans le flux par PublicHeader (iter 122).

// Branded 404 catch-all
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const TestimonialSubmitPage = lazy(() => import('./pages/TestimonialSubmitPage'));
const SuccessHub = lazy(() => import('./pages/SuccessHub'));
// PremiumLandingV2 removed — replaced by RevenueLanding
// import PremiumLandingV2 from './components/PremiumLandingV2';
const RevenueLanding = lazy(() => import('./pages/RevenueLanding'));
const GeoLanding = lazy(() => import('./pages/GeoLanding'));
const PartnersLanding = lazy(() => import('./pages/PartnersLanding'));

// Lazy loaded components for code splitting
const TaxSettings = lazy(() => import('./components/TaxSettings'));
const CurrencyToggle = lazy(() => import('./components/CurrencyToggle'));
const CurrencyConverter = lazy(() => import('./components/CurrencyConverter'));
const LanguageToggle = lazy(() => import('./components/LanguageToggle'));
const ThemeToggle = lazy(() => import('./components/ThemeToggle'));
const AccountingConnections = lazy(() => import('./components/AccountingConnections'));
const AccountingActivityLogs = lazy(() => import('./components/AccountingActivityLogs'));
const AccountingActivityLogsPage = lazy(() => import('./components/AccountingActivityLogsPage'));
const RoadmapPage = lazy(() => import('./components/RoadmapPage'));
const Logo = lazy(() => import('./components/LogoManager'));
const BrandLogo = lazy(() => import('./components/BrandLogo'));
const NewLandingPage = lazy(() => import('./components/NewLandingPage'));
const BillingSettings = lazy(() => import('./components/BillingSettings'));
const SalesLetter = lazy(() => import('./components/SalesLetter'));
const MarketplaceBrowser = lazy(() => import('./components/MarketplaceBrowser'));
const SellerDashboard = lazy(() => import('./components/SellerDashboard'));
const AdminMarketplacePanel = lazy(() => import('./components/AdminMarketplacePanel'));
const TranslationManager = lazy(() => import('./components/TranslationManager'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const TemplateSelector = lazy(() => import('./components/TemplateSelector'));
const AdvancedAnalytics = lazy(() => import('./components/AdvancedAnalytics'));
const TemplateCreatorOnboarding = lazy(() => import('./components/TemplateCreatorOnboarding'));
const CommunityFeed = lazy(() => import('./components/community/CommunityFeed'));
const CommunityTest = lazy(() => import('./components/community/CommunityTest'));
const UserProfilePage = lazy(() => import('./components/community/UserProfilePage'));
const DirectoryPage = lazy(() => import('./components/community/DirectoryPage'));
const GroupsPage = lazy(() => import('./components/community/GroupsPage'));
const AffiliateDashboard = lazy(() => import('./components/AffiliateDashboard'));
const AffiliatePublicPage = lazy(() => import('./components/AffiliatePublicPage'));
const ImprovedAffiliateDashboard = lazy(() => import('./components/affiliate/ImprovedAffiliateDashboardV2'));
const AffiliateDashboardV3 = lazy(() => import('./components/AffiliateDashboardV3'));
const AffiliateApplicationForm = lazy(() => import('./components/AffiliateApplicationForm'));
const AdminAffiliatePanel = lazy(() => import('./components/AdminAffiliatePanel'));
const AdminPayoutPanel = lazy(() => import('./components/AdminPayoutPanel'));
const NewAffiliateLP = lazy(() => import('./components/NewAffiliateLP'));
const SocialShareButtons = lazy(() => import('./components/SocialShareButtons'));
const MarketingResourcesLibrary = lazy(() => import('./components/MarketingResourcesLibrary'));
const AffiliateTierWidget = lazy(() => import('./components/AffiliateTierWidget'));
const AffiliateLeaderboard = lazy(() => import('./components/AffiliateLeaderboard'));
const SubAffiliateRecruitment = lazy(() => import('./components/SubAffiliateRecruitment'));
const InviteEarnWidget = lazy(() => import('./components/InviteEarnWidget'));
const OnboardingFlow = lazy(() => import('./components/OnboardingFlow'));
const GuidedTour = lazy(() => import('./components/GuidedTour'));
const DashboardChecklist = lazy(() => import('./components/DashboardChecklist'));
const AutomationsPage = lazy(() => import('./components/AutomationsPage'));
const LabsComingSoon = lazy(() => import('./components/LabsComingSoon'));
const AgencyDashboard = lazy(() => import('./components/AgencyDashboard'));
const WorkspaceSwitcher = lazy(() => import('./components/WorkspaceSwitcher'));
const PluginStore = lazy(() => import('./components/PluginStore'));
const XPDashboard = lazy(() => import('./components/XPDashboard'));
const CreditsWallet = lazy(() => import('./components/CreditsWallet'));
const Leaderboard = lazy(() => import('./components/Leaderboard'));
const MissionsPage = lazy(() => import('./components/MissionsPage'));
const AboutPage = lazy(() => import('./components/AboutPage'));
const ContactPage = lazy(() => import('./components/ContactPage'));
import CookieConsentBanner from './components/CookieConsentBanner';
const PricingPage = lazy(() => import('./components/PricingPage'));
const MarketplacePage = lazy(() => import('./components/MarketplacePage'));
const BoostDashboard = lazy(() => import('./components/BoostDashboard'));
const MonetizationAnalytics = lazy(() => import('./components/MonetizationAnalytics'));
const DeveloperPortal = lazy(() => import('./components/DeveloperPortal'));
const AutomationsAdmin = lazy(() => import('./components/automations/AutomationsAdmin'));
const PremiumAdminDashboard = lazy(() => import('./components/PremiumAdminDashboard'));
// iter-79: unified Admin Command Center (Phase A)
const AdminCommandCenter = lazy(() => import('./pages/AdminCommandCenter'));
const PremiumAffiliateDashboard = lazy(() => import('./components/PremiumAffiliateDashboard'));
// iter-71: canonical /affiliate-dashboard route component
const AffiliateEngineDashboard = lazy(() => import('./components/AffiliateEngineDashboard'));
const ReferralLanding = lazy(() => import('./pages/ReferralLanding'));
// iter-91: public one-click executive alert approval page
const ApproveAction = lazy(() => import('./pages/ApproveAction'));
// iter-92: integrations page (Google Business Profile)
const IntegrationsPage = lazy(() => import('./pages/IntegrationsPage'));
const DiscoveryPage = lazy(() => import('./pages/DiscoveryPage'));
// iter-72: Admin affiliate dashboard
const AdminAffiliateDashboard = lazy(() => import('./components/AdminAffiliateDashboard'));
const PremiumMarketplace = lazy(() => import('./components/PremiumMarketplace'));
const PremiumLayout = lazy(() => import('./components/PremiumLayout'));
const PluginMarketplace = lazy(() => import('./components/marketplace/PluginMarketplace'));
const PluginDetail = lazy(() => import('./components/marketplace/PluginDetail'));
const PluginPublish = lazy(() => import('./components/marketplace/PluginPublish'));
const CampaignsAdmin = lazy(() => import('./components/campaigns/CampaignsAdmin'));
const EventsAdmin = lazy(() => import('./components/events/EventsAdmin'));
const LeadMagnetPage = lazy(() => import('./components/LeadMagnetPage'));
const SalesPagePremium = lazy(() => import('./components/SalesPagePremium'));
const AdminFunnelAnalytics = lazy(() => import('./components/AdminFunnelAnalytics'));
const PremiumDashboard = lazy(() => import('./components/PremiumDashboard'));
const GrowthOS = lazy(() => import('./pages/GrowthOS'));
const AdminABTestingDashboard = lazy(() => import('./components/AdminABTestingDashboard'));
const AdminMonetizationDashboard = lazy(() => import('./components/AdminMonetizationDashboard'));
const UsageDashboard = lazy(() => import('./components/UsageDashboard'));
const RevenueForecasting = lazy(() => import('./components/RevenueForecasting'));
const CreditMarketplace = lazy(() => import('./components/CreditMarketplace'));
const OldAgencyResellerDashboard = lazy(() => import('./components/AgencyResellerDashboard'));
const InvestorReportsDashboard = lazy(() => import('./components/InvestorReportsDashboard'));
const AdminUsageBillingDashboard = lazy(() => import('./components/admin/AdminUsageBillingDashboard'));
const CreditGifting = lazy(() => import('./components/gifting/CreditGifting'));
const GiftClaimPage = lazy(() => import('./components/gifting/GiftClaimPage'));
const ReferralLeaderboard = lazy(() => import('./components/gifting/ReferralLeaderboard'));
const ShareableProfile = lazy(() => import('./components/gifting/ShareableProfile'));
const ReferralLeaderboardAdmin = lazy(() => import('./components/admin/ReferralLeaderboardAdmin'));
const NotificationAdmin = lazy(() => import('./components/admin/NotificationAdmin'));
const NotificationBell = lazy(() => import('./components/notifications/NotificationBell'));
const AdminPushPanel = lazy(() => import('./components/admin/AdminPushPanel'));
const GrowthEngineDashboard = lazy(() => import('./components/admin/GrowthEngineDashboard'));
const RestaurantAcquisitionDashboard = lazy(() => import('./components/admin/RestaurantAcquisitionDashboard'));
const ReferralDashboard = lazy(() => import('./components/admin/ReferralDashboard'));
const AdminReferralConfig = lazy(() => import('./components/admin/AdminReferralConfig'));
const AIOutboundDashboard = lazy(() => import('./components/admin/AIOutboundDashboard'));
const AgencyResellerDashboard = lazy(() => import('./components/admin/AgencyResellerDashboard'));
const AISDRDashboard = lazy(() => import('./components/admin/AISDRDashboard'));
const FollowUpDashboard = lazy(() => import('./components/admin/FollowUpDashboard'));
const PsychologyDashboard = lazy(() => import('./components/admin/PsychologyDashboard'));
const PsychologyABTestingDashboard = lazy(() => import('./components/admin/ABTestingDashboard'));
const AIChatWidget = lazy(() => import('./components/AIChatWidget'));
// RestaurantLanding (offre tarifaire legacy) supprimée — 301 vers / (iter 96)
const OutboundAnalyticsDashboard = lazy(() => import('./components/OutboundAnalyticsDashboard'));
const RevenueRecoveryDashboard = lazy(() => import('./components/RevenueRecoveryDashboard'));
const WeeklyAuditPage = lazy(() => import('./pages/WeeklyAuditPage'));
const CustomerImportPage = lazy(() => import('./pages/CustomerImportPage'));
const ScorecardPreview = lazy(() => import('./pages/ScorecardPreview'));
const SharedScoreRedirect = lazy(() => import('./pages/SharedScoreRedirect'));
const OnboardingWizard = lazy(() => import('./pages/OnboardingWizard'));
const DemoPage = lazy(() => import('./pages/DemoPage'));
const InteractiveDemo = lazy(() => import('./pages/InteractiveDemo'));
const LegalPages = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.TermsOfService })));
const LegalPrivacy = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.PrivacyPolicy })));
const LegalRefund = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.RefundPolicy })));
const LegalAcceptableUse = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.AcceptableUsePolicy })));
const LegalCookies = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.CookiePolicy })));
const LegalNoticePage = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.LegalNotice })));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen bg-slate-900 flex items-center justify-center">
    <div className="text-center">
      <div className="w-10 h-10 border-3 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-slate-400 text-sm">Loading...</p>
    </div>
  </div>
);


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const { changeLanguage, setLanguageFromProfile } = useLanguage();

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      
      // Set user's preferred language from their profile ONLY when the user
      // has no explicit local choice — never override the header switcher.
      const explicit = localStorage.getItem('app_language');
      if (response.data.language_preference && !explicit) {
        console.log(`🌍 Setting language from user profile: ${response.data.language_preference}`);
        setLanguageFromProfile(response.data.language_preference);
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = (newToken) => {
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, loading, fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }

  return children;
};

// Wrapper components to pass userId from auth context
const CreditMarketplaceWrapper = () => {
  const { user } = useAuth();
  return <CreditMarketplace userId={user?.email || user?.id} />;
};

const AgencyResellerDashboardWrapper = () => {
  const { user } = useAuth();
  return <AgencyResellerDashboard userId={user?.email || user?.id} />;
};

const CreditGiftingWrapper = () => {
  const { user } = useAuth();
  return <CreditGifting userId={user?.email || user?.id} />;
};

const ReferralLeaderboardWrapper = () => {
  const { user } = useAuth();
  return <ReferralLeaderboard userId={user?.email || user?.id} />;
};

const ReferralDashboardWrapper = () => {
  const { auditId } = useParams();
  return <ReferralDashboard auditId={auditId} />;
};

// Home Landing Page Component
const Home = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <BrandLogo 
              variant="responsive"
              size="default" 
              clickable={true} 
              href="/" 
              showText={true} 
              glowEffect={true}
              className="logo-hover-effect"
            />
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => window.location.href = '/automations'}
                className="text-slate-700 hover:text-purple-600"
              >
                <Zap className="w-4 h-4 mr-2" />
                Automations
              </Button>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => window.location.href = '/community'}
                className="text-slate-700 hover:text-blue-600"
              >
                <Users className="w-4 h-4 mr-2" />
                {t('navigation.community', 'Community')}
              </Button>
              
              <ThemeToggle variant="icon" size="sm" />
              <LanguageToggle variant="button" />
              <Button 
                variant="outline" 
                onClick={() => navigate('/login')}
              >
                {t('navigation.signIn')}
              </Button>
              <Button 
                onClick={() => navigate('/register')} 
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium px-6"
              >
                {t('navigation.getStarted')}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-20 pb-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center space-x-2 bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium mb-8">
            <Zap className="w-4 h-4" />
            <span>{t('landing.tagline')}</span>
          </div>
          <div className="flex flex-col items-center space-y-4 mb-6">
            <BrandLogo 
              variant="main"
              size="hero" 
              clickable={false} 
              showText={false} 
              glowEffect={true}
              className="mb-4"
            />
            <h1 className="text-5xl md:text-6xl font-bold text-slate-900 leading-tight text-center">
              {t('landing.title')}
              <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent"> {t('landing.titleHighlight')}</span>
            </h1>
          </div>
          <p className="text-xl text-slate-600 mb-10 max-w-2xl mx-auto leading-relaxed">
            {t('landing.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              onClick={() => navigate('/register')}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium px-8 py-4 text-lg"
            >
              {t('landing.startFreeTrial')}
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-slate-300 text-slate-700 hover:bg-slate-50 px-8 py-4 text-lg"
              onClick={() => navigate('/pricing')}
            >
              {t('landing.viewPricing', 'View Pricing')}
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 bg-white/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {t('landing.featuresTitle')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('landing.featuresSubtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                key: 'aiTemplates',
                icon: <FileText className="w-6 h-6 text-white" />,
                gradient: 'from-green-500 to-emerald-500'
              },
              {
                key: 'clientManagement',
                icon: <Users className="w-6 h-6 text-white" />,
                gradient: 'from-purple-500 to-violet-500'
              },
              {
                key: 'paymentTracking',
                icon: <DollarSign className="w-6 h-6 text-white" />,
                gradient: 'from-orange-500 to-red-500'
              }
            ].map((feature) => (
              <Card key={feature.key} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-white/80 backdrop-blur-sm">
                <CardHeader>
                  <div className={`w-12 h-12 bg-gradient-to-r ${feature.gradient} rounded-lg flex items-center justify-center mb-4`}>
                    {feature.icon}
                  </div>
                  <CardTitle className="text-xl">{t(`landing.features.${feature.key}.title`)}</CardTitle>
                  <CardDescription>
                    {t(`landing.features.${feature.key}.description`)}
                  </CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Sales Letter Section - A/B Testing */}
      <SalesLetter />

      {/* Pricing Section */}
      <PricingSection />

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-blue-600 to-indigo-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {t('landing.ctaTitle')}
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            {t('landing.ctaSubtitle')}
          </p>
          <Button 
            size="lg" 
            onClick={() => navigate('/register')}
            className="bg-white text-blue-600 hover:bg-blue-50 font-medium px-8 py-4 text-lg shadow-lg"
          >
            {t('landing.ctaButton')}
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="col-span-2">
              <div className="mb-4">
                <Logo 
                  size="small" 
                  clickable={false} 
                  showText={true} 
                  glowEffect={false}
                  className=""
                />
              </div>
              <p className="text-slate-300 mb-4 max-w-md">
                {t('landing.subtitle')}
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/roadmap" className="text-slate-300 hover:text-white transition-colors">
                    Roadmap
                  </Link>
                </li>
                <li>
                  <Link to="/login" className="text-slate-300 hover:text-white transition-colors">
                    {t('navigation.signIn')}
                  </Link>
                </li>
                <li>
                  <Link to="/register" className="text-slate-300 hover:text-white transition-colors">
                    {t('navigation.getStarted')}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Language Selector */}
            <div>
              <h4 className="font-semibold mb-4">Language</h4>
              <LanguageToggle variant="button" />
            </div>
          </div>
          
          <div className="border-t border-slate-700 mt-8 pt-8 text-center text-slate-400">
            <p>&copy; 2026 Copilote AI Business. All rights reserved.</p>
          </div>
        </div>
      </footer>

      <Toaster />
    </div>
  );
};

// Pricing Section Component
const PricingSection = () => {
  const navigate = useNavigate();
  const { t, getTranslations } = useLanguage();
  
  const plans = ['free', 'pro', 'business'].map(planId => {
    const planData = getTranslations(`pricing.plans.${planId}`);
    return {
      id: planId,
      name: planData.name || planId.charAt(0).toUpperCase() + planId.slice(1),
      price: planData.price || '€0',
      period: planData.period || 'month',
      description: planData.description || '',
      features: planData.features || [],
      cta: planData.cta || 'Get Started',
      popular: planId === 'business' // Changed to business as most popular
    };
  });

  return (
    <section className="py-20 px-6 bg-slate-50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            {t('landing.pricingTitle')}
          </h2>
          <p className="text-xl text-slate-600">
            {t('landing.pricingSubtitle')}
          </p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8">
          {plans.map((plan) => (
            <Card key={plan.id} className={`relative ${plan.popular ? 'border-blue-500 shadow-lg' : 'border-slate-200'}`}>
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-blue-600 text-white px-4 py-1">{t('pricing.mostPopular')}</Badge>
                </div>
              )}
              <CardHeader className="text-center">
                <CardTitle className="text-2xl">{plan.name}</CardTitle>
                <div className="mt-4">
                  <span className="text-4xl font-bold">{plan.price}</span>
                  <span className="text-slate-600 ml-2">/{plan.period}</span>
                </div>
                <CardDescription className="mt-2">{plan.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button 
                  className={`w-full ${plan.popular ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                  variant={plan.popular ? "default" : "outline"}
                  onClick={() => navigate('/register')}
                >
                  {plan.cta}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// Login Component
const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const { t } = useLanguage();

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  const validateForm = () => {
    const newErrors = {};
    
    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      login(response.data.access_token);
      toast.success(t('auth.welcomeMessage'));
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    window.location.href = "https://auth.emergentagent.com/?redirect=" + 
      encodeURIComponent(window.location.origin + "/auth/google/callback");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mb-6">
            <Logo 
              size="large" 
              clickable={false} 
              showText={true} 
              glowEffect={true}
              className="mx-auto"
            />
          </div>
          <div className="flex justify-between items-center mb-4">
            <div className="flex-1">
              <CardTitle className="text-2xl">{t('auth.welcomeBack')}</CardTitle>
              <CardDescription>{t('auth.signInAccount')}</CardDescription>
            </div>
            <LanguageToggle variant="button" />
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errors.email) {
                    setErrors({ ...errors, email: null });
                  }
                }}
                className={errors.email ? 'border-red-500' : ''}
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">{errors.email}</p>
              )}
            </div>
            <div>
              <Label htmlFor="password">{t('auth.password')}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (errors.password) {
                    setErrors({ ...errors, password: null });
                  }
                }}
                className={errors.password ? 'border-red-500' : ''}
              />
              {errors.password && (
                <p className="text-red-500 text-sm mt-1">{errors.password}</p>
              )}
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? t('auth.signingIn') : t('auth.signIn')}
            </Button>
          </form>
          
          <div className="mt-4">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <Separator />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-slate-500">{t('auth.orContinueWith')}</span>
              </div>
            </div>
            <Button 
              variant="outline" 
              className="w-full mt-4"
              onClick={handleGoogleLogin}
            >
              <Globe className="w-4 h-4 mr-2" />
              {t('auth.google')}
            </Button>
          </div>
          
          <div className="mt-4 text-center text-sm">
            {t('auth.dontHaveAccount')}
            <Link to="/register" className="text-blue-600 hover:underline">
              {t('auth.register')}
            </Link>
          </div>
        </CardContent>
      </Card>
      <Toaster />
    </div>
  );
};

// Register Component
const Register = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    company: ''
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const { t } = useLanguage();

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/register`, formData);
      const token = response.data.access_token;
      login(token);
      
      // Get user ID from token (decode JWT)
      const payload = JSON.parse(atob(token.split('.')[1]));
      const userEmail = payload.sub;
      
      // Fetch user to get ID
      const userResponse = await axios.get(`${API}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const userId = userResponse.data.id;
      
      // Initialize onboarding
      try {
        await axios.post(`${API}/onboarding/initialize?user_id=${userId}`, {}, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } catch (initError) {
        console.error('Error initializing onboarding:', initError);
      }
      
      toast.success(t('auth.accountCreated'));
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.registrationFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    window.location.href = "https://auth.emergentagent.com/?redirect=" + 
      encodeURIComponent(window.location.origin + "/auth/google/callback");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mb-6">
            <Logo 
              size="large" 
              clickable={false} 
              showText={true} 
              glowEffect={true}
              className="mx-auto"
            />
          </div>
          <div className="flex justify-between items-center mb-4">
            <div className="flex-1">
              <CardTitle className="text-2xl">{t('auth.createAccount')}</CardTitle>
              <CardDescription>{t('auth.startFreeTrial')}</CardDescription>
            </div>
            <LanguageToggle variant="button" />
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">{t('auth.fullName')}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="company">{t('auth.company')}</Label>
              <Input
                id="company"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="password">{t('auth.password')}</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                minLength={6}
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? t('auth.creatingAccount') : t('auth.signUp')}
            </Button>
          </form>
          
          <div className="mt-4">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <Separator />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-slate-500">{t('auth.orContinueWith')}</span>
              </div>
            </div>
            <Button 
              variant="outline" 
              className="w-full mt-4"
              onClick={handleGoogleLogin}
            >
              <Globe className="w-4 h-4 mr-2" />
              {t('auth.google')}
            </Button>
          </div>
          
          <div className="mt-4 text-center text-sm">
            {t('auth.alreadyHaveAccount')}
            <Link to="/login" className="text-blue-600 hover:underline">
              {t('auth.login')}
            </Link>
          </div>
        </CardContent>
      </Card>
      <Toaster />
    </div>
  );
};

// Google OAuth Callback Component
const GoogleCallback = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t, currentLanguage } = useLanguage();
  const processed = useRef(false);

  useEffect(() => {
    // Emergent Auth playbook : échange du session_id UNE seule fois (StrictMode / changement de langue post-login)
    if (processed.current) return;
    processed.current = true;
    const processGoogleAuth = async () => {
      const urlFragment = window.location.hash;
      const sessionIdMatch = urlFragment.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        
        try {
          // iter-123 : body JSON {session_id, locale} — la langue UI est conservée pour le nouveau compte
          const response = await axios.post(`${API}/auth/google/callback`, { session_id: sessionId, locale: currentLanguage });
          login(response.data.access_token);
          toast.success(t('auth.googleSuccess'));
          // iter-102 LOT1: first login (no Discovery yet) → guided activation
          try {
            const d = await axios.get(`${API}/discovery/latest`, {
              headers: { Authorization: `Bearer ${response.data.access_token}` },
            });
            navigate(d.data?.session ? '/dashboard' : '/discovery?welcome=1');
          } catch {
            navigate('/dashboard');
          }
        } catch (error) {
          toast.error(authErrorMessage(error, t, 'auth.googleAuthFailed'));
          navigate(`/login?lang=${currentLanguage}`);
        }
      } else {
        navigate(`/login?lang=${currentLanguage}`);
      }
    };

    processGoogleAuth();
  }, [navigate, login, t, currentLanguage]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p>{t('auth.processing')}</p>
      </div>
    </div>
  );
};

// Dashboard Component with multilingual support
const Dashboard = () => {
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const { user, logout } = useAuth();
  const { t } = useLanguage();

  const fetchData = async () => {
    try {
      const [clientsRes, invoicesRes, subscriptionRes] = await Promise.all([
        axios.get(`${API}/clients`),
        axios.get(`${API}/invoices`),
        axios.get(`${API}/subscription/info`)
      ]);
      
      setClients(clientsRes.data);
      setInvoices(invoicesRes.data);
      setSubscriptionInfo(subscriptionRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const checkOnboardingStatus = async () => {
    if (!user) return;
    
    try {
      const response = await axios.get(`${API}/onboarding/status?user_id=${user.id}`);
      if (response.data.needs_onboarding) {
        setShowOnboarding(true);
      } else {
        // Check if needs tour
        const tourRes = await axios.get(`${API}/onboarding/tour/status?user_id=${user.id}`);
        if (tourRes.data.needs_tour) {
          setShowTour(true);
        }
      }
    } catch (error) {
      console.error('Error checking onboarding status:', error);
    }
  };

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    setShowTour(true); // Start tour after onboarding
  };

  const handleTourComplete = () => {
    setShowTour(false);
  };

  useEffect(() => {
    fetchData();
    checkOnboardingStatus();
  }, []);

  const handleUpgrade = async (planId) => {
    try {
      const response = await axios.post(`${API}/subscription/checkout`, { plan_id: planId });
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(t('subscription.checkoutError'));
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <Logo 
              size="small" 
              clickable={true} 
              href="/" 
              showText={true} 
              glowEffect={true}
              className="logo-hover-effect"
            />
            
            <div className="flex items-center space-x-4">
              {user && <WorkspaceSwitcher userId={user.id} onWorkspaceChange={(ws) => console.log('Workspace changed:', ws)} />}
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => window.location.href = '/automations'}
                className="text-slate-700 hover:text-purple-600"
              >
                <Zap className="w-4 h-4 mr-2" />
                Automations
              </Button>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => window.location.href = '/community'}
                className="text-slate-700 hover:text-blue-600"
              >
                <Users className="w-4 h-4 mr-2" />
                {t('navigation.community', 'Community')}
              </Button>
              
              <LanguageToggle variant="button" />
              
              {subscriptionInfo && (
                <div className="flex items-center space-x-2">
                  <Badge 
                    variant={subscriptionInfo.current_plan === 'free' ? 'secondary' : 'default'}
                    className={subscriptionInfo.current_plan === 'free' ? '' : 'bg-gradient-to-r from-blue-600 to-indigo-600'}
                  >
                    {subscriptionInfo.current_plan === 'free' && <span>{t('dashboard.freePlan')}</span>}
                    {subscriptionInfo.current_plan === 'pro' && (
                      <>
                        <Crown className="w-3 h-3 mr-1" />
                        {t('dashboard.proPlan')}
                      </>
                    )}
                    {subscriptionInfo.current_plan === 'business' && (
                      <>
                        <Crown className="w-3 h-3 mr-1" />
                        {t('dashboard.businessPlan')}
                      </>
                    )}
                  </Badge>
                  {subscriptionInfo.current_plan === 'free' && (
                    <Button 
                      size="sm" 
                      onClick={() => handleUpgrade('pro')}
                      className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                    >
                      {t('navigation.upgrade')}
                    </Button>
                  )}
                </div>
              )}
              
              <Button 
                variant="outline" 
                size="sm"
                onClick={logout}
              >
                <LogOut className="w-4 h-4 mr-2" />
                {t('navigation.logout')}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Subscription Alert */}
        {subscriptionInfo && subscriptionInfo.current_plan === 'free' && subscriptionInfo.clients_used >= subscriptionInfo.clients_limit && (
          <Alert className="mb-6 border-orange-200 bg-orange-50">
            <AlertCircle className="h-4 w-4 text-orange-600" />
            <AlertDescription className="text-orange-800">
              {t('dashboard.alerts.clientLimitReached', { limit: subscriptionInfo.clients_limit })}
              <Button 
                variant="link" 
                className="p-0 h-auto text-orange-600 hover:text-orange-800 ml-2"
                onClick={() => handleUpgrade('pro')}
              >
                {t('navigation.upgrade')}
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Layout: Sidebar + Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar - Checklist Widget */}
          <div className="lg:col-span-1">
            {user && <DashboardChecklist userId={user.id} />}
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">{t('dashboard.stats.totalClients')}</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {clients.length}
                    {subscriptionInfo && subscriptionInfo.clients_limit !== 999 && (
                      <span className="text-sm font-normal text-slate-500">
                        /{subscriptionInfo.clients_limit}
                      </span>
                    )}
                  </p>
                </div>
                <Users className="w-8 h-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">{t('dashboard.stats.totalInvoices')}</p>
                  <p className="text-2xl font-bold text-slate-900">{invoices.length}</p>
                </div>
                <FileText className="w-8 h-8 text-green-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">{t('dashboard.stats.paidInvoices')}</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {invoices.filter(inv => inv.status === 'paid').length}
                  </p>
                </div>
                <DollarSign className="w-8 h-8 text-emerald-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">{t('dashboard.stats.overdue')}</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {invoices.filter(inv => inv.status === 'overdue').length}
                  </p>
                </div>
                <Calendar className="w-8 h-8 text-red-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Tabs defaultValue="invoices" className="space-y-6">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="invoices">{t('dashboard.tabs.invoices')}</TabsTrigger>
            <TabsTrigger value="clients">{t('dashboard.tabs.clients')}</TabsTrigger>
            <TabsTrigger value="marketplace">{t('dashboard.tabs.marketplace', 'Marketplace')}</TabsTrigger>
            <TabsTrigger value="create">{t('dashboard.tabs.createInvoice')}</TabsTrigger>
            <TabsTrigger value="settings">{t('dashboard.tabs.settings')}</TabsTrigger>
          </TabsList>
          
          <TabsContent value="invoices">
            <InvoicesList invoices={invoices} onRefresh={fetchData} />
          </TabsContent>
          
          <TabsContent value="clients">
            <ClientsList 
              clients={clients} 
              onRefresh={fetchData} 
              subscriptionInfo={subscriptionInfo}
            />
          </TabsContent>
          
          <TabsContent value="marketplace">
            <Card>
              <CardHeader>
                <CardTitle>{t('marketplace.title', 'Template Marketplace')}</CardTitle>
                <CardDescription>{t('marketplace.subtitle', 'Browse and purchase professional templates')}</CardDescription>
              </CardHeader>
              <CardContent>
                <MarketplaceBrowser embedded={true} />
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="create">
            <CreateInvoice 
              clients={clients} 
              subscriptionInfo={subscriptionInfo}
              onSuccess={() => {
                fetchData();
                toast.success(t('createInvoice.invoiceCreated'));
              }} 
            />
          </TabsContent>
          
          <TabsContent value="settings">
            <SettingsPanel user={user} onRefresh={fetchData} />
          </TabsContent>
        </Tabs>
          </div>
        </div>
      </div>

      {/* Onboarding Flow Modal */}
      {showOnboarding && user && (
        <OnboardingFlow 
          userId={user.id} 
          onComplete={handleOnboardingComplete}
        />
      )}

      {/* Guided Tour */}
      {showTour && user && !showOnboarding && (
        <GuidedTour 
          userId={user.id} 
          onComplete={handleTourComplete}
        />
      )}

      {/* Invite & Earn Widget */}
      {user && <InviteEarnWidget userId={user.id} />}

      <Toaster />
    </div>
  );
};

// Enhanced Invoices List Component with multilingual support
const InvoicesList = ({ invoices, onRefresh }) => {
  const { t } = useLanguage();
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'sent': return 'bg-blue-100 text-blue-800';
      case 'overdue': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const downloadPDF = async (invoiceId, invoiceNumber) => {
    try {
      const response = await axios.get(`${API}/invoices/${invoiceId}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(t('invoices.downloadPdf'));
    } catch (error) {
      toast.error(t('invoices.downloadError'));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('invoices.title')}</CardTitle>
        <CardDescription>{t('invoices.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {invoices.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>{t('invoices.noInvoices')}</p>
            </div>
          ) : (
            invoices.map((invoice) => (
              <div key={invoice.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50">
                <div>
                  <h3 className="font-medium">{invoice.invoice_number}</h3>
                  <p className="text-sm text-slate-600">{invoice.client_info.name}</p>
                  <p className="text-xs text-slate-500">{t('invoices.due')} {invoice.due_date}</p>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="text-right">
                    <p className="font-semibold">€{invoice.total_amount.toFixed(2)}</p>
                    <Badge className={`text-xs ${getStatusColor(invoice.status)}`}>
                      {t(`invoices.status.${invoice.status}`)}
                    </Badge>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => downloadPDF(invoice.id, invoice.invoice_number)}
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Enhanced Clients List Component with multilingual support
const ClientsList = ({ clients, onRefresh, subscriptionInfo }) => {
  const [showAddClient, setShowAddClient] = useState(false);
  const [newClient, setNewClient] = useState({
    name: '',
    email: '',
    company: '',
    address: '',
    phone: ''
  });
  const { t } = useLanguage();

  const canAddClient = () => {
    if (!subscriptionInfo) return false;
    return subscriptionInfo.clients_used < subscriptionInfo.clients_limit || 
           subscriptionInfo.clients_limit === 999;
  };

  const handleAddClient = async (e) => {
    e.preventDefault();
    
    if (!canAddClient()) {
      toast.error(t('clients.clientLimitError'));
      return;
    }
    
    try {
      await axios.post(`${API}/clients`, newClient);
      setNewClient({ name: '', email: '', company: '', address: '', phone: '' });
      setShowAddClient(false);
      onRefresh();
      toast.success(t('clients.clientAdded'));
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(t('clients.clientLimitError'));
      } else {
        toast.error(t('clients.clientAddError'));
      }
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>{t('clients.title')}</CardTitle>
          <CardDescription>{t('clients.description')}</CardDescription>
        </div>
        <Dialog open={showAddClient} onOpenChange={setShowAddClient}>
          <DialogTrigger asChild>
            <Button disabled={!canAddClient()}>
              <Plus className="w-4 h-4 mr-2" />
              {t('clients.addClient')}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('clients.addNewClient')}</DialogTitle>
              <DialogDescription>{t('clients.createProfile')}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddClient} className="space-y-4">
              <div>
                <Label htmlFor="name">{t('clients.nameRequired')}</Label>
                <Input
                  id="name"
                  value={newClient.name}
                  onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="email">{t('clients.emailRequired')}</Label>
                <Input
                  id="email"
                  type="email"
                  value={newClient.email}
                  onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="company">{t('clients.company')}</Label>
                <Input
                  id="company"
                  value={newClient.company}
                  onChange={(e) => setNewClient({ ...newClient, company: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="address">{t('clients.address')}</Label>
                <Textarea
                  id="address"
                  value={newClient.address}
                  onChange={(e) => setNewClient({ ...newClient, address: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="phone">{t('clients.phone')}</Label>
                <Input
                  id="phone"
                  value={newClient.phone}
                  onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                />
              </div>
              <Button type="submit" className="w-full">{t('clients.addButton')}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {clients.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>{t('clients.noClients')}</p>
            </div>
          ) : (
            clients.map((client) => (
              <div key={client.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50">
                <div>
                  <h3 className="font-medium">{client.name}</h3>
                  <p className="text-sm text-slate-600">{client.email}</p>
                  {client.company && <p className="text-sm text-slate-500">{client.company}</p>}
                </div>
                <Button variant="outline" size="sm">
                  <Eye className="w-4 h-4 mr-2" />
                  {t('clients.view')}
                </Button>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Enhanced Create Invoice Component with multilingual support and language-aware AI
const CreateInvoice = ({ clients, subscriptionInfo, onSuccess }) => {
  const [selectedClient, setSelectedClient] = useState('');
  const [items, setItems] = useState([{ description: '', quantity: 1, unit_price: 0, total: 0 }]);
  const [taxRate, setTaxRate] = useState(0);
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAITemplate, setShowAITemplate] = useState(false);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [aiRequest, setAiRequest] = useState({
    business_type: '',
    service_description: '',
    client_info: ''
  });
  
  const { t, currentLanguage } = useLanguage();

  const canUseAI = () => {
    return subscriptionInfo?.features?.ai_features || false;
  };

  const applyMarketplaceTemplate = (template) => {
    try {
      // Parse template content
      const templateData = JSON.parse(template.content);
      
      // Apply template items to invoice
      if (templateData.items && Array.isArray(templateData.items)) {
        const templateItems = templateData.items.map(item => ({
          description: item.description || '',
          quantity: item.quantity || 1,
          unit_price: item.unit_price || 0,
          total: (item.quantity || 1) * (item.unit_price || 0)
        }));
        setItems(templateItems);
      }
      
      // Apply template notes
      if (templateData.notes) {
        setNotes(templateData.notes);
      }
      
      // Apply tax rate if exists
      if (templateData.tax_rate) {
        setTaxRate(templateData.tax_rate);
      }
      
      toast.success(t('createInvoice.templateApplied') || 'Template applied successfully!');
    } catch (error) {
      console.error('Error applying template:', error);
      toast.error(t('createInvoice.templateError') || 'Error applying template');
    }
  };

  const updateItemTotal = (index, field, value) => {
    const updatedItems = [...items];
    updatedItems[index][field] = value;
    
    if (field === 'quantity' || field === 'unit_price') {
      updatedItems[index].total = updatedItems[index].quantity * updatedItems[index].unit_price;
    }
    
    setItems(updatedItems);
  };

  const addItem = () => {
    setItems([...items, { description: '', quantity: 1, unit_price: 0, total: 0 }]);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const generateAITemplate = async () => {
    if (!canUseAI()) {
      toast.error(t('createInvoice.aiFeatureRequired'));
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/ai/generate-template`, {
        ...aiRequest,
        language: currentLanguage // Pass current language to API
      });
      const aiData = response.data;
      
      // Convert AI response to invoice items
      const aiItems = aiData.invoice_items.map(item => ({
        description: item.description,
        quantity: item.quantity,
        unit_price: item.unit_price,
        total: item.quantity * item.unit_price
      }));
      
      setItems(aiItems);
      setNotes(aiData.professional_notes);
      setShowAITemplate(false);
      toast.success(t('createInvoice.aiTemplateGenerated'));
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(t('createInvoice.aiFeatureRequired'));
      } else {
        toast.error(t('createInvoice.aiTemplateError'));
      }
    }
    setLoading(false);
  };

  const calculateSubtotal = () => {
    return items.reduce((sum, item) => sum + item.total, 0);
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const tax = subtotal * (taxRate / 100);
    return subtotal + tax;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedClient || items.length === 0) {
      toast.error(t('createInvoice.selectClientError'));
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/invoices`, {
        client_id: selectedClient,
        items,
        tax_rate: taxRate,
        due_date: dueDate,
        notes
      });
      
      // Reset form
      setSelectedClient('');
      setItems([{ description: '', quantity: 1, unit_price: 0, total: 0 }]);
      setTaxRate(0);
      setDueDate('');
      setNotes('');
      
      onSuccess();
    } catch (error) {
      toast.error(t('createInvoice.invoiceCreateError'));
    }
    setLoading(false);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>{t('createInvoice.title')}</CardTitle>
          <CardDescription>{t('createInvoice.description')}</CardDescription>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            type="button"
            onClick={() => setShowTemplateSelector(true)}
          >
            <FileText className="w-4 h-4 mr-2" />
            {t('createInvoice.applyTemplate') || 'Apply Template'}
          </Button>
          {canUseAI() && (
            <Dialog open={showAITemplate} onOpenChange={setShowAITemplate}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Zap className="w-4 h-4 mr-2" />
                  {t('createInvoice.aiTemplate')}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t('createInvoice.aiTemplateTitle')}</DialogTitle>
                  <DialogDescription>{t('createInvoice.aiTemplateDescription')}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="business_type">{t('createInvoice.businessType')}</Label>
                    <Input
                      id="business_type"
                      placeholder={t('createInvoice.businessTypePlaceholder')}
                      value={aiRequest.business_type}
                      onChange={(e) => setAiRequest({ ...aiRequest, business_type: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="service_description">{t('createInvoice.serviceDescriptionLabel')}</Label>
                    <Textarea
                      id="service_description"
                      placeholder={t('createInvoice.serviceDescriptionPlaceholder')}
                      value={aiRequest.service_description}
                      onChange={(e) => setAiRequest({ ...aiRequest, service_description: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="client_info">{t('createInvoice.clientContext')}</Label>
                    <Input
                      id="client_info"
                      placeholder={t('createInvoice.clientContextPlaceholder')}
                      value={aiRequest.client_info}
                      onChange={(e) => setAiRequest({ ...aiRequest, client_info: e.target.value })}
                    />
                  </div>
                  <Button onClick={generateAITemplate} disabled={loading} className="w-full">
                    {loading ? t('createInvoice.generating') : t('createInvoice.generateTemplate')}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {showTemplateSelector && (
          <TemplateSelector
            onTemplateSelect={applyMarketplaceTemplate}
            onClose={() => setShowTemplateSelector(false)}
          />
        )}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Client Selection */}
          <div>
            <Label htmlFor="client">{t('createInvoice.selectClient')}</Label>
            <Select value={selectedClient} onValueChange={setSelectedClient}>
              <SelectTrigger>
                <SelectValue placeholder={t('createInvoice.chooseClient')} />
              </SelectTrigger>
              <SelectContent>
                {clients.map((client) => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.name} - {client.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Invoice Items */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <Label>{t('createInvoice.invoiceItems')}</Label>
              <Button type="button" onClick={addItem} variant="outline" size="sm">
                <Plus className="w-4 h-4 mr-2" />
                {t('createInvoice.addItem')}
              </Button>
            </div>
            
            <div className="space-y-4">
              {items.map((item, index) => (
                <div key={index} className="grid grid-cols-12 gap-4 items-end p-4 border rounded-lg">
                  <div className="col-span-5">
                    <Label htmlFor={`desc-${index}`}>{t('createInvoice.description')}</Label>
                    <Input
                      id={`desc-${index}`}
                      value={item.description}
                      onChange={(e) => updateItemTotal(index, 'description', e.target.value)}
                      placeholder={t('createInvoice.serviceDescription')}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor={`qty-${index}`}>{t('createInvoice.quantity')}</Label>
                    <Input
                      id={`qty-${index}`}
                      type="number"
                      step="0.1"
                      value={item.quantity}
                      onChange={(e) => updateItemTotal(index, 'quantity', parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor={`price-${index}`}>{t('createInvoice.unitPrice')}</Label>
                    <Input
                      id={`price-${index}`}
                      type="number"
                      step="0.01"
                      value={item.unit_price}
                      onChange={(e) => updateItemTotal(index, 'unit_price', parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>{t('createInvoice.total')}</Label>
                    <Input value={`€${item.total.toFixed(2)}`} readOnly />
                  </div>
                  <div className="col-span-1">
                    {items.length > 1 && (
                      <Button type="button" onClick={() => removeItem(index)} variant="outline" size="sm">
                        ×
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tax and Totals */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <Label htmlFor="tax_rate">{t('createInvoice.taxRate')}</Label>
              <Input
                id="tax_rate"
                type="number"
                step="0.1"
                value={taxRate}
                onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)}
              />
            </div>
            <div>
              <Label htmlFor="due_date">{t('createInvoice.dueDate')}</Label>
              <Input
                id="due_date"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="notes">{t('createInvoice.notes')}</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('createInvoice.notesPlaceholder')}
            />
          </div>

          {/* Invoice Summary */}
          <div className="bg-slate-50 p-6 rounded-lg">
            <h3 className="font-semibold mb-4">{t('createInvoice.invoiceSummary')}</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>{t('createInvoice.subtotal')}</span>
                <span>€{calculateSubtotal().toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>{t('createInvoice.tax', { rate: taxRate })}</span>
                <span>€{(calculateSubtotal() * (taxRate / 100)).toFixed(2)}</span>
              </div>
              <Separator />
              <div className="flex justify-between font-semibold text-lg">
                <span>{t('createInvoice.totalAmount')}</span>
                <span>€{calculateTotal().toFixed(2)}</span>
              </div>
            </div>
          </div>

          <Button type="submit" disabled={loading} className="w-full">
            {loading ? t('createInvoice.creating') : t('createInvoice.createButton')}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

// Settings Panel Component with language settings
const SettingsPanel = ({ user, onRefresh }) => {
  const [companyProfile, setCompanyProfile] = useState({
    company_name: '',
    company_address: '',
    company_phone: '',
    company_email: '',
    vat_number: '',
    logo_url: ''
  });
  const [loading, setLoading] = useState(false);
  const { t, currentLanguage, changeLanguage } = useLanguage();
  const { fetchUser } = useAuth();

  useEffect(() => {
    if (user?.company_profile) {
      setCompanyProfile(user.company_profile);
    }
  }, [user]);

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/auth/profile`, companyProfile);
      toast.success(t('settings.profileUpdated'));
      onRefresh();
    } catch (error) {
      toast.error(t('settings.profileUpdateError'));
    } finally {
      setLoading(false);
    }
  };

  const handleLanguageChange = async (language) => {
    try {
      // Update language immediately in the UI
      changeLanguage(language, 'user');
      
      // If user is authenticated, save to their profile
      if (user) {
        await axios.put(`${API}/auth/language`, { language });
        await fetchUser(); // Refresh user data to get updated language preference
        console.log(`🌍 Language saved to user profile: ${language}`);
      }
      
      toast.success(t('settings.languageUpdated'));
    } catch (error) {
      console.error('Failed to update language:', error);
      toast.error(t('settings.profileUpdateError'));
    }
  };

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Building className="w-5 h-5 mr-2" />
            {t('settings.companyProfile')}
          </CardTitle>
          <CardDescription>
            {t('settings.companyProfileDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="company_name">{t('settings.companyName')}</Label>
            <Input
              id="company_name"
              value={companyProfile.company_name}
              onChange={(e) => setCompanyProfile({ ...companyProfile, company_name: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="company_email">{t('settings.companyEmail')}</Label>
            <Input
              id="company_email"
              type="email"
              value={companyProfile.company_email}
              onChange={(e) => setCompanyProfile({ ...companyProfile, company_email: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="company_phone">{t('settings.companyPhone')}</Label>
            <Input
              id="company_phone"
              value={companyProfile.company_phone}
              onChange={(e) => setCompanyProfile({ ...companyProfile, company_phone: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="company_address">{t('settings.companyAddress')}</Label>
            <Textarea
              id="company_address"
              value={companyProfile.company_address}
              onChange={(e) => setCompanyProfile({ ...companyProfile, company_address: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="vat_number">{t('settings.vatNumber')}</Label>
            <Input
              id="vat_number"
              value={companyProfile.vat_number}
              onChange={(e) => setCompanyProfile({ ...companyProfile, vat_number: e.target.value })}
            />
          </div>
          <Button onClick={handleSave} disabled={loading} className="w-full">
            {loading ? t('settings.saving') : t('settings.saveProfile')}
          </Button>
        </CardContent>
      </Card>

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Globe className="w-5 h-5 mr-2" />
              {t('settings.language')}
            </CardTitle>
            <CardDescription>
              {t('settings.languageDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <LanguageToggle variant="grid" onLanguageChange={handleLanguageChange} />
              <div className="text-sm text-slate-600 mt-2">
                <p>{t('settings.languageDescription')}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <CreditCard className="w-5 h-5 mr-2" />
              {t('settings.subscriptionManagement')}
            </CardTitle>
            <CardDescription>
              {t('settings.subscriptionDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <SubscriptionManagement />
          </CardContent>
        </Card>
      </div>
      
      <div className="md:col-span-2">
        <AccountingConnections />
      </div>
    </div>
  );
};

// Subscription Management Component with multilingual support
const SubscriptionManagement = () => {
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t, getTranslations } = useLanguage();

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API}/subscription/info`);
      setSubscriptionInfo(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planId) => {
    try {
      const response = await axios.post(`${API}/subscription/checkout`, { plan_id: planId });
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(t('subscription.checkoutError'));
    }
  };

  if (loading) {
    return <div>{t('common.loading')}</div>;
  }

  const plans = getTranslations('subscription.plans');

  return (
    <div className="space-y-4">
      {subscriptionInfo && (
        <div className="p-4 bg-slate-50 rounded-lg">
          <h3 className="font-semibold">
            {t('settings.currentPlan', { plan: subscriptionInfo.current_plan.toUpperCase() })}
          </h3>
          <p className="text-sm text-slate-600">
            {t('settings.status', { status: subscriptionInfo.status })}
          </p>
          <p className="text-sm text-slate-600">
            {t('settings.clientsUsage', {
              used: subscriptionInfo.clients_used,
              limit: subscriptionInfo.clients_limit === 999 ? t('settings.unlimited') : subscriptionInfo.clients_limit
            })}
          </p>
        </div>
      )}
      
      {subscriptionInfo?.current_plan === 'free' && (
        <div className="space-y-3">
          {Object.entries(plans).map(([planId, planData]) => (
            <Card key={planId} className="p-4">
              <div className="flex justify-between items-center">
                <div>
                  <h4 className="font-semibold">{planData.name}</h4>
                  <p className="text-sm text-slate-600">{planData.price}</p>
                  <ul className="text-xs text-slate-500 mt-1">
                    {planData.features.map((feature, idx) => (
                      <li key={idx}>• {feature}</li>
                    ))}
                  </ul>
                </div>
                <Button 
                  size="sm"
                  onClick={() => handleUpgrade(planId)}
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                >
                  {t('settings.upgradeButton')}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// Subscription Success Page with multilingual support
const SubscriptionSuccess = () => {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const navigate = useNavigate();
  const { t } = useLanguage();

  useEffect(() => {
    const checkPaymentStatus = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get('session_id');

      // The session_id is informational only — the source of truth is the
      // server-side rr_subscriptions doc updated by the Stripe webhook.
      // We poll the current user's billing/subscription record until it
      // flips to 'active' (or 'trialing'), then forward to /dashboard.
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      let attempts = 0;
      const maxAttempts = 15;  // 15 × 2s = 30s grace for the webhook to land

      const pollStatus = async () => {
        try {
          const response = await axios.get(
            `${API}/api/revenue-engine/billing/subscription`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const sub = response.data || {};
          if (sub.status === 'active' || sub.status === 'trialing') {
            setStatus('success');
            setLoading(false);
            setTimeout(() => navigate('/dashboard'), 3000);
          } else if (attempts < maxAttempts) {
            attempts++;
            setTimeout(pollStatus, 2000);
          } else {
            setStatus('timeout');
            setLoading(false);
          }
        } catch (error) {
          // 404 = no sub yet; keep polling. Other errors = surface.
          if (error?.response?.status === 404 && attempts < maxAttempts) {
            attempts++;
            setTimeout(pollStatus, 2000);
          } else if (attempts < maxAttempts) {
            attempts++;
            setTimeout(pollStatus, 2000);
          } else {
            setStatus('error');
            setLoading(false);
          }
        }
      };

      // Tiny delay so the webhook has a head-start before our first poll
      setTimeout(pollStatus, 1500);
      // Hint: log the session_id for support tickets
      if (sessionId) {
        // eslint-disable-next-line no-console
        console.info('[subscription-success] session_id:', sessionId);
      }
    };

    checkPaymentStatus();
  }, [navigate, t]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>{t('subscription.processingPayment')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-blue-50">
      <Card className="w-full max-w-md text-center">
        <CardContent className="p-8">
          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-green-800 mb-2">{t('subscription.success.title')}</h2>
              <p className="text-slate-600 mb-4">
                {t('subscription.success.description')}
              </p>
              <Button onClick={() => navigate('/dashboard')}>
                {t('subscription.success.button')}
              </Button>
            </>
          )}
          
          {status === 'error' && (
            <>
              <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-red-800 mb-2">{t('subscription.error.title')}</h2>
              <p className="text-slate-600 mb-4">
                {t('subscription.error.description')}
              </p>
              <Button onClick={() => navigate('/dashboard')}>
                {t('subscription.error.button')}
              </Button>
            </>
          )}
          
          {status === 'timeout' && (
            <>
              <AlertCircle className="w-16 h-16 text-orange-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-orange-800 mb-2">{t('subscription.processing.title')}</h2>
              <p className="text-slate-600 mb-4">
                {t('subscription.processing.description')}
              </p>
              <Button onClick={() => navigate('/dashboard')}>
                {t('subscription.processing.button')}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Chat Widget Wrapper - Conditionally shows AIChatWidget
// Hides on the new restaurant-funnel public pages (landing/demo/pricing/scorecard/legal)
// AND on /restaurants (which has its own RestaurantChatWidget). The legacy AIChatWidget
// is English-only and uses EUR pricing — it does NOT match the Copilote AI Business
// (CHF, FR/DE/IT) brand and would actively hurt conversion on those pages.
const ChatWidgetWrapper = () => {
  const isHidden = (path) => {
    const hiddenExact = ['/', '/demo', '/demo-interactive', '/pricing', '/scorecard-preview', '/restaurants',
                         '/terms', '/privacy', '/refund', '/acceptable-use', '/cookies', '/legal-notice',
                         '/login', '/register'];
    // Iter 126 — bot de PRÉ-VENTE (grille € du module SDR, textes anglais) : jamais sur les
    // surfaces authentifiées du restaurateur/admin (périmètre devise d'affichage).
    const hiddenPrefixes = ['/approve/', '/dashboard', '/growth-os', '/revenue-recovery',
                            '/weekly-audit', '/help', '/discovery', '/admin'];
    return hiddenExact.includes(path) || hiddenPrefixes.some((p) => path.startsWith(p));
  };

  const [showWidget, setShowWidget] = React.useState(() => !isHidden(window.location.pathname));

  React.useEffect(() => {
    const checkRoute = () => setShowWidget(!isHidden(window.location.pathname));
    const interval = setInterval(checkRoute, 200);
    window.addEventListener('popstate', checkRoute);
    return () => {
      clearInterval(interval);
      window.removeEventListener('popstate', checkRoute);
    };
  }, []);

  if (!showWidget) return null;

  return (
    <Suspense fallback={null}>
      <AIChatWidget />
    </Suspense>
  );
};

// Main App Component wrapped with LanguageProvider
function App() {
  // Force re-render on iOS Safari back/forward (bfcache)
  React.useEffect(() => {
    const handler = (e) => { if (e.persisted) window.location.reload(); };
    window.addEventListener('pageshow', handler);
    return () => window.removeEventListener('pageshow', handler);
  }, []);

  return (
    <LanguageProvider>
    <RegionProvider>
    <DisplayCurrencyProvider>
    <ABTestingProvider>
      <LanguageHead />
      <AuthProvider>
        <div className="App">
          <BrowserRouter>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public Landing & Marketing Pages */}
                <Route path="/" element={<RevenueLanding />} />
                {/* Iter 96 — landing pages géolocalisées (SEO international,
                    contenu unique par marché, jamais de redirection IP) */}
                {Object.values(GEO_MARKETS).map((m) => (
                  <Route key={m.slug} path={m.path} element={<GeoLanding market={m} />} />
                ))}
                {/* Legacy invoicing-SaaS landings — redirect to the active restaurant landing.
                    These pages carried "10,000+ users" / "7-day trial" copy that conflicted with
                    the current product positioning and trial length (14 days). See iteration_57 audit. */}
                <Route path="/v1" element={<Navigate to="/" replace />} />
                <Route path="/free-guide" element={<LeadMagnetPage />} />
                <Route path="/sales" element={<Navigate to="/" replace />} />
                <Route path="/affiliate" element={<Navigate to="/" replace />} />
                {/* Iter 96 — ancienne offre tarifaire retirée : redirection permanente */}
                <Route path="/restaurants" element={<Navigate to="/" replace />} />
                <Route path="/partners" element={<PartnersLanding />} />
                {/* iter-71: public referral landing — sets cb_ref cookie + redirects to /demo */}
                <Route path="/r/:code" element={<ReferralLanding />} />
                {/* iter-91: public one-click alert approval (token-secured, confirm-first) */}
                <Route path="/approve/:token" element={<ApproveAction />} />
                
                {/* Legal & Info Pages */}
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/privacy" element={<LegalPrivacy />} />
                <Route path="/terms" element={<LegalPages />} />
                <Route path="/refund" element={<LegalRefund />} />
                <Route path="/acceptable-use" element={<LegalAcceptableUse />} />
                <Route path="/cookies" element={<LegalCookies />} />
                <Route path="/legal-notice" element={<LegalNoticePage />} />
                <Route path="/gdpr" element={<Navigate to="/privacy" replace />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/roadmap" element={<RoadmapPage />} />
                
                {/* Public Marketplace */}
                <Route path="/marketplace" element={<PremiumMarketplace />} />
                
                {/* Auth Pages */}
                <Route path="/login" element={<PremiumLogin />} />
                <Route path="/register" element={<PremiumRegister />} />
                <Route path="/signup" element={<SignupRedirect />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/auth/google/callback" element={<GoogleCallback />} />
                <Route path="/subscription/success" element={<SubscriptionSuccess />} />
                
                {/* Protected: Growth OS is the flagship landing after login */}
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <GrowthOS />
                  </ProtectedRoute>
                } />
                {/* iter-92: integrations area (Google Business Profile) */}
                <Route path="/integrations" element={
                  <ProtectedRoute>
                    <IntegrationsPage />
                  </ProtectedRoute>
                } />
                {/* iter-94: Executive Business Discovery */}
                <Route path="/discovery" element={
                  <ProtectedRoute>
                    <DiscoveryPage />
                  </ProtectedRoute>
                } />
                <Route path="/growth-os" element={
                  <ProtectedRoute>
                    <GrowthOS />
                  </ProtectedRoute>
                } />
                <Route path="/dashboard-classic" element={
                  <ProtectedRoute>
                    <PremiumDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/dashboard/billing" element={
                  <ProtectedRoute>
                    <BillingSettings />
                  </ProtectedRoute>
                } />
                {/* v2: anciennes destinations nav — redirections propres (zéro 404) */}
                <Route path="/settings" element={<Navigate to="/dashboard/billing" replace />} />
                <Route path="/analytics" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard/marketplace" element={
                  <ProtectedRoute>
                    <SellerDashboard />
                  </ProtectedRoute>
                } />
                
                {/* Protected: Admin Routes */}
                <Route path="/admin" element={
                  <ProtectedRoute>
                    <AdminCommandCenter />
                  </ProtectedRoute>
                } />
                <Route path="/admin/legacy" element={
                  <ProtectedRoute>
                    <PremiumAdminDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/admin/marketplace" element={
                  <ProtectedRoute>
                    <AdminMarketplacePanel />
                  </ProtectedRoute>
                } />
                <Route path="/admin/translations" element={
                  <ProtectedRoute>
                    <TranslationManager />
                  </ProtectedRoute>
                } />
                <Route path="/admin/analytics" element={
                  <ProtectedRoute>
                    <AdvancedAnalytics />
                  </ProtectedRoute>
                } />
              <Route path="/community" element={
                <ProtectedRoute>
                  <LabsComingSoon module="community" />
                </ProtectedRoute>
              } />
              <Route path="/community/profile/:userId" element={
                <ProtectedRoute>
                  <LabsComingSoon module="community" />
                </ProtectedRoute>
              } />
              <Route path="/community/profile" element={
                <ProtectedRoute>
                  <LabsComingSoon module="community" />
                </ProtectedRoute>
              } />
              <Route path="/community/directory" element={
                <ProtectedRoute>
                  <LabsComingSoon module="community" />
                </ProtectedRoute>
              } />
              <Route path="/community/groups" element={
                <ProtectedRoute>
                  <LabsComingSoon module="community" />
                </ProtectedRoute>
              } />
              <Route path="/growth/affiliate" element={
                <ProtectedRoute>
                  <AffiliateEngineDashboard />
                </ProtectedRoute>
              } />
              <Route path="/affiliate-dashboard" element={
                <ProtectedRoute>
                  <AffiliateEngineDashboard />
                </ProtectedRoute>
              } />
              <Route path="/admin/affiliates" element={
                <ProtectedRoute>
                  <AdminAffiliateDashboard />
                </ProtectedRoute>
              } />
              <Route path="/affiliate/apply" element={
                <ProtectedRoute>
                  <AffiliateApplicationForm />
                </ProtectedRoute>
              } />
              <Route path="/admin/affiliates" element={
                <ProtectedRoute>
                  <AdminAffiliatePanel />
                </ProtectedRoute>
              } />
              <Route path="/admin/payouts" element={
                <ProtectedRoute>
                  <AdminPayoutPanel />
                </ProtectedRoute>
              } />
              <Route path="/automations" element={
                <ProtectedRoute>
                  <LabsComingSoon module="automations" />
                </ProtectedRoute>
              } />

              <Route path="/affiliate/resources" element={
                <ProtectedRoute>
                  <MarketingResourcesLibrary affiliateId="current" />
                </ProtectedRoute>
              } />
              <Route path="/affiliate/leaderboard" element={
                <ProtectedRoute>
                  <AffiliateLeaderboard affiliateId="current" />
                </ProtectedRoute>
              } />
              <Route path="/affiliate/recruitment" element={
                <ProtectedRoute>
                  <SubAffiliateRecruitment affiliateId="current" />
                </ProtectedRoute>
              } />

              <Route path="/growth/xp" element={
                <ProtectedRoute>
                  <XPDashboard />
                </ProtectedRoute>
              } />
              <Route path="/growth/credits" element={
                <ProtectedRoute>
                  <CreditsWallet />
                </ProtectedRoute>
              } />
              <Route path="/growth/leaderboard" element={
                <ProtectedRoute>
                  <Leaderboard />
                </ProtectedRoute>
              } />
              <Route path="/growth/missions" element={
                <ProtectedRoute>
                  <MissionsPage />
                </ProtectedRoute>
              } />
              <Route path="/boost" element={
                <ProtectedRoute>
                  <BoostDashboard />
                </ProtectedRoute>
              } />
              <Route path="/analytics/monetization" element={
                <ProtectedRoute>
                  <MonetizationAnalytics />
                </ProtectedRoute>
              } />
              <Route path="/developers" element={
                <ProtectedRoute>
                  <DeveloperPortal />
                </ProtectedRoute>
              } />
              <Route path="/agency" element={
                <ProtectedRoute>
                  <AgencyDashboard />
                </ProtectedRoute>
              } />
              
              <Route path="/plugins" element={
                <ProtectedRoute>
                  <PluginStore />
                </ProtectedRoute>
              } />

              <Route path="/admin/automations" element={
                <ProtectedRoute>
                  <AutomationsAdmin />
                </ProtectedRoute>
              } />
              <Route path="/admin/campaigns" element={
                <ProtectedRoute>
                  <CampaignsAdmin />
                </ProtectedRoute>
              } />
              <Route path="/admin/events" element={
                <ProtectedRoute>
                  <EventsAdmin />
                </ProtectedRoute>
              } />
              <Route path="/admin/funnel" element={
                <ProtectedRoute>
                  <AdminFunnelAnalytics />
                </ProtectedRoute>
              } />
              <Route path="/admin/ab-testing" element={
                <ProtectedRoute>
                  <AdminABTestingDashboard />
                </ProtectedRoute>
              } />
              <Route path="/admin/monetization" element={
                <ProtectedRoute>
                  <AdminMonetizationDashboard />
                </ProtectedRoute>
              } />
              <Route path="/admin/usage-billing" element={
                <ProtectedRoute>
                  <AdminUsageBillingDashboard />
                </ProtectedRoute>
              } />
              <Route path="/admin/forecasting" element={
                <ProtectedRoute>
                  <RevenueForecasting />
                </ProtectedRoute>
              } />
              <Route path="/admin/investor-reports" element={
                <ProtectedRoute>
                  <InvestorReportsDashboard />
                </ProtectedRoute>
              } />
              
              {/* Protected: Marketplace (Edit/Publish) */}
              <Route path="/marketplace/publish" element={
                <ProtectedRoute>
                  <PluginPublish />
                </ProtectedRoute>
              } />
              <Route path="/marketplace/onboarding" element={
                <ProtectedRoute>
                  <TemplateCreatorOnboarding />
                </ProtectedRoute>
              } />
              
              {/* Public: Marketplace Plugin Detail */}
              <Route path="/marketplace/plugins/:pluginId" element={<PluginDetail />} />
              
              {/* Credit Marketplace */}
              <Route path="/credits" element={<CreditMarketplaceWrapper />} />
              
              {/* Credit Gifting */}
              <Route path="/gifting" element={
                <ProtectedRoute>
                  <CreditGiftingWrapper />
                </ProtectedRoute>
              } />
              
              {/* Gift Claim Page (Public) */}
              <Route path="/claim-gift" element={<GiftClaimPage />} />
              
              {/* Referral Leaderboard */}
              <Route path="/leaderboard" element={<ReferralLeaderboardWrapper />} />
              
              {/* Shareable Referral Profile (Public) */}
              <Route path="/referral-profile/:userId" element={<ShareableProfile />} />
              
              {/* Leaderboard Admin */}
              <Route path="/admin/leaderboard" element={
                <ProtectedRoute>
                  <ReferralLeaderboardAdmin />
                </ProtectedRoute>
              } />
              
              {/* Notification Admin */}
              <Route path="/admin/notifications" element={
                <ProtectedRoute>
                  <NotificationAdmin />
                </ProtectedRoute>
              } />
              
              {/* Push Notifications Admin */}
              <Route path="/admin/push-notifications" element={
                <ProtectedRoute>
                  <AdminPushPanel token={localStorage.getItem('token')} />
                </ProtectedRoute>
              } />
              
              {/* AI Growth Engine Dashboard */}
              <Route path="/admin/growth-engine" element={
                <ProtectedRoute>
                  <GrowthEngineDashboard token={localStorage.getItem('token')} />
                </ProtectedRoute>
              } />

              <Route path="/admin/restaurant-engine" element={
                <ProtectedRoute>
                  <RestaurantAcquisitionDashboard />
                </ProtectedRoute>
              } />

              <Route path="/referral/:auditId" element={
                <ReferralDashboardWrapper />
              } />

              <Route path="/admin/referral-config" element={
                <ProtectedRoute>
                  <AdminReferralConfig />
                </ProtectedRoute>
              } />

              <Route path="/admin/outbound" element={
                <ProtectedRoute>
                  <AIOutboundDashboard />
                </ProtectedRoute>
              } />

              <Route path="/agency-partner" element={
                <AgencyResellerDashboard />
              } />
              
              {/* Agency Reseller Dashboard (Credit Marketplace) */}
              <Route path="/agency-reseller" element={
                <ProtectedRoute>
                  <AgencyResellerDashboardWrapper />
                </ProtectedRoute>
              } />

              {/* AI SDR Dashboard */}
              <Route path="/admin/ai-sdr" element={
                <ProtectedRoute>
                  <AISDRDashboard />
                </ProtectedRoute>
              } />
              
              {/* Follow-Up Automation Dashboard */}
              <Route path="/admin/followup" element={
                <ProtectedRoute>
                  <FollowUpDashboard />
                </ProtectedRoute>
              } />
              
              {/* Psychology Engine Dashboard */}
              <Route path="/admin/psychology" element={
                <ProtectedRoute>
                  <PsychologyDashboard />
                </ProtectedRoute>
              } />
              
              {/* Psychology A/B Testing Dashboard */}
              <Route path="/admin/psychology-ab" element={
                <ProtectedRoute>
                  <PsychologyABTestingDashboard />
                </ProtectedRoute>
              } />
              
              <Route path="/admin/outbound-analytics" element={
                <ProtectedRoute>
                  <OutboundAnalyticsDashboard />
                </ProtectedRoute>
              } />

              <Route path="/revenue-recovery" element={
                <ProtectedRoute>
                  <RevenueRecoveryDashboard />
                </ProtectedRoute>
              } />

              <Route path="/weekly-audit/:restaurantId" element={
                <ProtectedRoute>
                  <WeeklyAuditPage />
                </ProtectedRoute>
              } />

              <Route path="/import-customers" element={
                <ProtectedRoute>
                  <CustomerImportPage />
                </ProtectedRoute>
              } />

              <Route path="/import-customers/:restaurantId" element={
                <ProtectedRoute>
                  <CustomerImportPage />
                </ProtectedRoute>
              } />

              <Route path="/scorecard-preview" element={
                <ScorecardPreview />
              } />

              {/* iter-122d — URL humaine courte d'un Score partagé (bascule future ; canonique = /api/s/{id}) */}
              <Route path="/s/:shareId" element={
                <SharedScoreRedirect />
              } />

              <Route path="/demo" element={
                <DemoPage />
              } />

              <Route path="/demo-interactive" element={
                <InteractiveDemo />
              } />

              <Route path="/temoignage" element={
                <TestimonialSubmitPage />
              } />
              <Route path="/testimonial" element={
                <TestimonialSubmitPage />
              } />

              <Route path="/help" element={
                <ProtectedRoute>
                  <SuccessHub />
                </ProtectedRoute>
              } />

              <Route path="/onboarding" element={
                <ProtectedRoute>
                  <OnboardingWizard />
                </ProtectedRoute>
              } />

              {/* Branded 404 — must remain the last route */}
              <Route path="*" element={<NotFoundPage />} />
              
            </Routes>
            
            {/* Iter 123 — un seul Toaster global : les toasts (erreurs serveur localisées, succès)
                doivent s'afficher sur toutes les routes, y compris /login /register /forgot-password. */}
            <Toaster richColors position="top-center" closeButton />

            {/* AI Chat Widget - Shows on all pages except /restaurants (which has its own) */}
            <ChatWidgetWrapper />

            {/* Cookie consent banner — gates PostHog/GA4 analytics */}
            <CookieConsentBanner />
            
            </Suspense>
          </BrowserRouter>
        </div>
      </AuthProvider>
    </ABTestingProvider>
    </DisplayCurrencyProvider>
    </RegionProvider>
    </LanguageProvider>
  );
}

export default App;
